import { ACTION_ERROR } from 'src/modules/assistant/constants/actions';
import { TActionDraft } from 'src/modules/assistant/types/message';
import {
    TActionHandler,
    TActionHandlerConfig,
    TActionResult,
    TExecuteOutcome,
} from './types';

export const actionFailure = (error: string): TActionResult => ({
    ok: false,
    error,
});

const normalizeResult = (result: TExecuteOutcome): TActionResult => {
    if (typeof result === 'object' && result !== null && 'ok' in result)
        return result;
    return result ? { ok: true } : actionFailure(ACTION_ERROR.EXECUTION_FAILED);
};

/**
 * Builds an action handler with a uniform contract:
 * - fields: editable draft fields ([name, labelKey, multiline])
 * - validate(draft): whether the draft can be executed
 * - execute(draft, deps): always resolves to { ok, data?, error? } and never throws
 */
export const createActionHandler = <TDraft extends TActionDraft>({
    fields = [],
    validate,
    execute,
    schedulable = false,
    acceptsDocuments = false,
    cancellable = false,
}: TActionHandlerConfig<TDraft>): TActionHandler<TDraft> =>
    Object.freeze({
        fields,
        schedulable,
        acceptsDocuments,
        cancellable,
        validate: (draft: TDraft) => Boolean(validate(draft)),
        execute: async (draft: TDraft, deps) => {
            if (!validate(draft))
                return actionFailure(ACTION_ERROR.INVALID_DRAFT);
            try {
                return normalizeResult(await execute(draft, deps));
            } catch (_) {
                return actionFailure(ACTION_ERROR.EXECUTION_FAILED);
            }
        },
    });
